import { computed } from 'vue'
import { FindexClient } from '@/integrations/findex'
import type { UseAssetFormState, UseAssetFormComputedData } from '../types'
import { uniqueBy } from 'math'
import { cryptoQuoteToOption } from '@/form/options/crypto'
import { pushToast } from 'components/toast/hooks'
import { defineField, Validators, type FormFieldDefinition } from '@/form/useForm'
import { getRefId, type ICryptoQuote, type ISavedDocument } from 'findef'
import { useApiHealth } from '@/modules/app/hooks/useApiHealth'

export const cryptoQuoteField = (): FormFieldDefinition<UseAssetFormState, ICryptoQuote | ISavedDocument<ICryptoQuote, string>, UseAssetFormComputedData> => {
  const { isFmpAvailable } = useApiHealth()

  return defineField({
    label: 'Crypto Name',
    type: 'dbObject',
    component: 'select2',
    validators: [Validators.required()],
    required: true,
    placeholder: 'Start typing to search...',
    needs: ['isEditMode'],
    next: (state) => ({
      locked: state.isEditMode === true
    }),
    options: {
      toOption: cryptoQuoteToOption,
      warning: computed(() =>
        isFmpAvailable.value === false ? 'Tickers are temporarily limited, contact team@myfindex.com for any questions' : undefined
      ),
      search: {
        api: {
          fetchOne: async (current) => {
            if (!current) return null;
            const [item] = await FindexClient.getCryptoQuoteById(getRefId(current));
            if (!item) return null;
            return cryptoQuoteToOption(item);
          },
          fetchAll: async () => {
            const [items] = await FindexClient.getCryptoQuotes({ limit: 100 })
            const options = uniqueBy(items || [], (it) => it.symbol).map((it) =>
              cryptoQuoteToOption(it)
            )
            return options
          },
          fetch: async (queryString) => {
            const [items] = await FindexClient.getCryptoQuotes({ queryString })
            const options = uniqueBy(items || [], (it) => it.symbol).map((it) =>
              cryptoQuoteToOption(it)
            )
            return options
          },
          fetchSuggestions: async () => {
            const [items] = await FindexClient.getCryptoQuotes({ limit: 100 })
            const options = uniqueBy(items || [], (it) => it.symbol).map((it) =>
              cryptoQuoteToOption(it)
            )
            return options
          }
        }
      },
      add: {
        invokeOnPressEnter: true,
        createItem: async (name) => {
          const [item] = await FindexClient.createCryptoQuote({
            name: name,
            prettyName: name,
            symbol: name,
            manuallyAdded: true
          })
          if (!item) {
            pushToast({
              title: 'Error',
              subtitle: 'Could not create crypto quote.',
              variant: 'error'
            })
            return undefined
          }
          return cryptoQuoteToOption(item)
        }
      }
    },
    injectedProps: {
      icon: {
        src: async () => import('assets/img/svg/icons/search.svg?raw'),
        stroke: 'currentColor',
        size: '1rem'
      }
    }
  })
}
